import {map} from 'rxjs/operators';
import {
  GetToolsRequestParams,
  HrToolImageResponse,
  HrToolMiniResponse,
  HrToolResponse,
  HrToolServiceModelResponse,
  HrToolTranslationResponse,
} from '@frontend/shared-api';
import {
  HrTool,
  HrToolFilters,
  HrToolImage,
  HrToolServiceModel,
  HrToolTranslation,
} from './models/hr-tool';
import {HrToolMini} from './models/hr-tool-mini';
import {CountryCode} from './models/country-code';
import {LanguageCode} from './models/language-code';
import {ModuleFunction} from './models/module-function';
import {FlattenedProjectStep, ProjectStep} from './models/project-step';
import {DirectusTranslation} from './models/translation';

export interface HrCatalogReferenceData {
  moduleFunctionsById: Map<number, ModuleFunction>;
  countryByIso: Map<string, CountryCode>;
  languageByIso: Map<string, LanguageCode>;
}

export function mapHrToolFilters(filters: HrToolFilters): GetToolsRequestParams {
  return {
    nameSearch: filters.nameSearch?.input?.trim() || undefined,
    languageCode: filters.nameSearch?.languageCode,
    functions: filters.functions,
    serviceModels: filters.serviceModels,
    availableCountries: filters.availableCountries,
    availableLanguages: filters.availableLanguages,
    companySize: filters.companySize,
    certifications: filters.certifications,
    active: filters.active,
    aiCapabilities: filters.aiCapabilities,
  };
}

function mapTranslations(
  translations: HrToolTranslationResponse[] | undefined,
  toolName: string,
): HrToolTranslation[] {
  // The tool name is the single source of truth; per-translation name no longer exists,
  // so every language variant reuses the tool name.
  return (translations ?? []).map((translation) => ({
    id: 0,
    relationship_id: '',
    language: translation.language ?? '',
    name: toolName,
    description: translation.description ?? '',
    description_detailed: translation.descriptionDetailed ?? '',
  }));
}

function mapServiceModels(serviceModels?: HrToolServiceModelResponse[]): HrToolServiceModel[] {
  return (serviceModels ?? []).map((serviceModel) => ({
    id: serviceModel.id ?? '',
    name: serviceModel.name ?? '',
    translationKey: serviceModel.translationKey ?? null,
  }));
}

function mapImages(images?: HrToolImageResponse[]): HrToolImage[] {
  return (images ?? []).map((image) => ({
    id: image.id ?? '',
    name: image.name ?? '',
    gallery_image: image.galleryImage ?? '',
  }));
}

function mapModuleFunctions(
  moduleFunctionIds: number[] | undefined,
  moduleFunctionsById: Map<number, ModuleFunction>,
): ModuleFunction[] {
  return (moduleFunctionIds ?? [])
    .map((id) => moduleFunctionsById.get(id))
    .filter((moduleFunction): moduleFunction is ModuleFunction => !!moduleFunction);
}

function mapCountries(
  isoCodes: string[] | undefined,
  countryByIso: Map<string, CountryCode>,
): CountryCode[] {
  return (isoCodes ?? [])
    .filter((isoCode) => !!isoCode)
    .map((isoCode) => countryByIso.get(isoCode) ?? {isoCode});
}

function mapLanguages(
  isoCodes: string[] | undefined,
  languageByIso: Map<string, LanguageCode>,
): LanguageCode[] {
  return (isoCodes ?? [])
    .filter((isoCode) => !!isoCode)
    .map((isoCode) => languageByIso.get(isoCode) ?? {isoCode});
}

export function mapHrToolResponse(
  tool: HrToolResponse,
  references: HrCatalogReferenceData,
): HrTool {
  return {
    id: tool.id ?? '',
    name: tool.name ?? '',
    toolProvider: {id: tool.toolProvider?.id ?? '', name: tool.toolProvider?.name ?? ''},
    coverImage: tool.coverImage ?? '',
    hostingProviderLocation: tool.hostingProviderLocation ?? '',
    architecturalPrinciple: tool.architecturalPrinciple,
    website: tool.website ?? null,
    images: mapImages(tool.images),
    serviceModels: mapServiceModels(tool.serviceModels),
    availableCountries: mapCountries(tool.availableCountries, references.countryByIso),
    availableLanguages: mapLanguages(tool.availableLanguages, references.languageByIso),
    moduleFunctions: mapModuleFunctions(tool.moduleFunctions, references.moduleFunctionsById),
    translations: mapTranslations(tool.translations, tool.name ?? ''),
    minEmployeeCount: tool.minEmployeeCount ?? null,
    maxEmployeeCount: tool.maxEmployeeCount ?? null,
    certificationDirectusUuids: tool.certificationDirectusUuids ?? [],
    aiCapabilities: tool.aiCapabilities ?? [],
  };
}

export function mapHrToolMiniResponse(tool: HrToolMiniResponse): HrToolMini {
  return {
    id: tool.id ?? '',
    name: tool.name ?? '',
    toolProvider: {id: tool.toolProvider?.id ?? '', name: tool.toolProvider?.name ?? ''},
    translations: mapTranslations(tool.translations, tool.name ?? ''),
  };
}

export function flattenProjectStepResponse() {
  return map((steps: ProjectStep[]): FlattenedProjectStep[] => {
    return steps.map((step) => {
      const defaultTitle =
        step.translations && step.translations.length > 0 ? step.translations[0].name || '' : '';

      const flattenedStep: FlattenedProjectStep = {
        id: step.id,
        title: defaultTitle,
        parent_step: step.parent || undefined,
        image: step.image,
        sort_order: step.sort_order,
        translations: step.translations,
        chapterConfigId: step.chapterConfigId,
      };

      return flattenedStep;
    });
  });
}

export function getDirectusTranslation(
  translations: DirectusTranslation[] | null | undefined,
  lang: string,
  fallback = '',
  field = 'name',
): string {
  if (!lang || !Array.isArray(translations) || translations?.length === 0) {
    return fallback;
  }

  const translation = translations?.find((t) => t.language === lang);
  const translatedValue = translation?.[field as keyof DirectusTranslation] as string;
  return (
    translatedValue ||
    (translations?.[0]?.[field as keyof DirectusTranslation] as string) ||
    fallback
  );
}
